import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { authAPI, oportunidadesAPI, actividadesAPI, clientesAPI, facturasAPI, presupuestosAPI } from '../services/api';
import { getCurrencyConfig, formatMoney } from '../utils/currency';
import './Oportunidades.css';

const ETAPAS = ['prospecto', 'contactado', 'reunion', 'propuesta', 'negociacion', 'ganada', 'perdida', 'pausada'];
const ORIGENES = ['web', 'referido', 'marketing', 'cold_call', 'evento', 'otro'];
const PRIORIDADES = ['baja', 'media', 'alta', 'urgente'];
const TIPOS_ACTIVIDAD = ['llamada', 'email', 'reunion', 'seguimiento', 'propuesta', 'otro'];

function Oportunidades() {
  const [view, setView] = useState('pipeline'); // pipeline | list | detail | form
  const [user, setUser] = useState(null);
  const [oportunidades, setOportunidades] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [selectedOportunidad, setSelectedOportunidad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filtros
  const [filtroEtapa] = useState(''); // Preparado para futuros filtros
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroPrioridad, setFiltroPrioridad] = useState('');
  const [busquedaRapida, setBusquedaRapida] = useState('');

  // Formularios
  const [form, setForm] = useState({
    cliente_id: '',
    titulo: '',
    descripcion: '',
    valor_estimado: 0,
    probabilidad: 50,
    etapa: 'prospecto',
    fecha_estimada_cierre: '',
    origen: 'otro',
    prioridad: 'media',
    notas: '',
    factura_id: '',
    presupuesto_id: '',
  });

  const [formActividad, setFormActividad] = useState({
    tipo: 'seguimiento',
    titulo: '',
    descripcion: '',
    fecha_programada: '',
  });

  const [showActividadForm, setShowActividadForm] = useState(false);
  const [facturas, setFacturas] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  const [stats, setStats] = useState(null);
  const [draggedOportunidad, setDraggedOportunidad] = useState(null);
  const [dragOverEtapa, setDragOverEtapa] = useState(null);
  const [showMotivoPerdidaModal, setShowMotivoPerdidaModal] = useState(false);
  const [oportunidadParaPerder, setOportunidadParaPerder] = useState(null);
  const [motivoPerdida, setMotivoPerdida] = useState('');
  
  // Filtros avanzados
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  const [filtroOrigen, setFiltroOrigen] = useState('');
  const currency = getCurrencyConfig(user);
  const money = (value) => formatMoney(value, { ...currency, decimals: 2 });

  const loadClientes = useCallback(async () => {
    try {
      const r = await clientesAPI.getAll(true);
      setClientes(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setClientes([]);
    }
  }, []);

  const loadFacturasYPresupuestos = useCallback(async () => {
    try {
      const [fRes, pRes] = await Promise.all([
        facturasAPI.getAll().catch(() => ({ data: [] })),
        presupuestosAPI.getAll().catch(() => ({ data: [] })),
      ]);
      const fData = Array.isArray(fRes.data) ? fRes.data : (fRes.data?.data || []);
      const pData = Array.isArray(pRes.data) ? pRes.data : (pRes.data?.data || []);
      setFacturas(fData);
      setPresupuestos(pData);
    } catch (e) {
      setFacturas([]);
      setPresupuestos([]);
    }
  }, []);

  const loadOportunidades = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const filters = {};
      if (filtroEtapa) filters.etapa = filtroEtapa;
      if (filtroCliente) filters.cliente_id = filtroCliente;
      if (filtroPrioridad) filters.prioridad = filtroPrioridad;
      if (filtroFechaDesde) filters.fecha_desde = filtroFechaDesde;
      if (filtroFechaHasta) filters.fecha_hasta = filtroFechaHasta;
      if (filtroOrigen) filters.origen = filtroOrigen;

      const r = await oportunidadesAPI.getAll(filters);

      // Verificar estructura de respuesta
      if (r && r.data) {
        if (Array.isArray(r.data)) {
          setOportunidades(r.data);
        } else if (r.data.success && Array.isArray(r.data.data)) {
          setOportunidades(r.data.data);
        } else {
          setOportunidades([]);
        }
      } else {
        setOportunidades([]);
      }
    } catch (e) {
      const errorMsg = e.response?.data?.error || e.message || 'Error al cargar oportunidades';
      setError(errorMsg);
      setOportunidades([]);
    } finally {
      setLoading(false);
    }
    }, [filtroEtapa, filtroCliente, filtroPrioridad, filtroFechaDesde, filtroFechaHasta, filtroOrigen]);

  const loadStats = useCallback(async () => {
    try {
      const r = await oportunidadesAPI.getStats();
      // La API devuelve {success: true, data: {...}}
      if (r.data && r.data.data) {
        setStats(r.data.data);
      } else if (r.data) {
        setStats(r.data);
      } else {
        setStats(null);
      }
    } catch (e) {
      setStats(null);
    }
  }, []);

  useEffect(() => {
    authAPI.check().then((response) => {
      if (response.data?.authenticated && response.data?.user) {
        setUser(response.data.user);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadClientes();
    loadOportunidades();
    loadStats();
    loadFacturasYPresupuestos();
  }, [loadClientes, loadOportunidades, loadStats, loadFacturasYPresupuestos]);

  const getClienteNombre = (id) => {
    const c = clientes.find((x) => x.id === id);
    return c ? `${c.nombre} ${c.apellidos}` : '';
  };

  const valorPonderado = (o) => (Number(o.valor_estimado) * Number(o.probabilidad)) / 100;

  const oportunidadesFiltradasLista = useMemo(() => {
    if (!busquedaRapida.trim()) return oportunidades;
    const q = busquedaRapida.trim().toLowerCase();
    const getNombre = (o) => {
      const c = clientes.find((x) => x.id === o.cliente_id);
      return c ? `${c.nombre} ${c.apellidos}` : ((o.cliente_nombre || '') + ' ' + (o.cliente_apellidos || '')).trim();
    };
    return oportunidades.filter((o) => {
      const titulo = (o.titulo || '').toLowerCase();
      const cliente = getNombre(o).toLowerCase();
      const etapa = (o.etapa || '').toLowerCase();
      const prioridad = (o.prioridad || '').toLowerCase();
      return titulo.includes(q) || cliente.includes(q) || etapa.includes(q) || prioridad.includes(q);
    });
  }, [oportunidades, busquedaRapida, clientes]);

  const handleNuevaOportunidad = () => {
    setSelectedOportunidad(null);
    setForm({
      cliente_id: '',
      titulo: '',
      descripcion: '',
      valor_estimado: 0,
      probabilidad: 50,
      etapa: 'prospecto',
      fecha_estimada_cierre: '',
      origen: 'otro',
      prioridad: 'media',
      notas: '',
      factura_id: '',
      presupuesto_id: '',
    });
    setView('form');
  };

  const handleVerOportunidad = async (oportunidad) => {
    try {
      setLoading(true);
      const r = await oportunidadesAPI.getById(oportunidad.id);
      setSelectedOportunidad(r.data);
      setView('detail');
    } catch (e) {
      setError('Error al cargar la oportunidad');
    } finally {
      setLoading(false);
    }
  };

  const handleEditarOportunidad = () => {
    if (!selectedOportunidad) return;
    setForm({
      cliente_id: String(selectedOportunidad.cliente_id),
      titulo: selectedOportunidad.titulo || '',
      descripcion: selectedOportunidad.descripcion || '',
      valor_estimado: Number(selectedOportunidad.valor_estimado) || 0,
      probabilidad: Number(selectedOportunidad.probabilidad) || 50,
      etapa: selectedOportunidad.etapa || 'prospecto',
      fecha_estimada_cierre: selectedOportunidad.fecha_estimada_cierre || '',
      origen: selectedOportunidad.origen || 'otro',
      prioridad: selectedOportunidad.prioridad || 'media',
      notas: selectedOportunidad.notas || '',
      factura_id: selectedOportunidad.factura_id ? String(selectedOportunidad.factura_id) : '',
      presupuesto_id: selectedOportunidad.presupuesto_id ? String(selectedOportunidad.presupuesto_id) : '',
    });
    setView('form');
  };

  const handleSaveOportunidad = async () => {
    if (!form.cliente_id || !form.titulo.trim()) {
      setError('Cliente y título son requeridos');
      return;
    }
    if (form.valor_estimado < 0) {
      setError('El valor estimado no puede ser negativo');
      return;
    }
    if (form.probabilidad < 0 || form.probabilidad > 100) {
      setError('La probabilidad debe estar entre 0% y 100%');
      return;
    }

    setError(null);
    try {
      const payload = {
        ...form,
        valor_estimado: Number(form.valor_estimado),
        probabilidad: Number(form.probabilidad),
        factura_id: form.factura_id ? Number(form.factura_id) : null,
        presupuesto_id: form.presupuesto_id ? Number(form.presupuesto_id) : null,
      };

      if (selectedOportunidad) {
        await oportunidadesAPI.update(selectedOportunidad.id, payload);
        setSuccess('Oportunidad actualizada');
      } else {
        await oportunidadesAPI.create(payload);
        setSuccess('Oportunidad creada');
      }

      // Recargar datos antes de cambiar la vista
      await Promise.all([loadOportunidades(), loadStats()]);
      setView('pipeline');
      setSelectedOportunidad(null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar la oportunidad');
    }
  };

  const handleCambiarEtapa = async (oportunidadId, nuevaEtapa, motivoPerdida = null) => {
    try {
      await oportunidadesAPI.updateEtapa(oportunidadId, nuevaEtapa, motivoPerdida);
      setSuccess(`Oportunidad marcada como ${nuevaEtapa}`);
      loadOportunidades();
      loadStats();
      if (selectedOportunidad && selectedOportunidad.id === oportunidadId) {
        const r = await oportunidadesAPI.getById(oportunidadId);
        setSelectedOportunidad(r.data);
      }
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cambiar etapa');
    }
  };

  const handleConfirmarPerdida = async () => {
    if (!oportunidadParaPerder) return;
    if (!motivoPerdida.trim()) {
      setError('Debes indicar un motivo de pérdida');
      return;
    }
    try {
      await handleCambiarEtapa(oportunidadParaPerder.id, 'perdida', motivoPerdida);
      setShowMotivoPerdidaModal(false);
      setOportunidadParaPerder(null);
      setMotivoPerdida('');
    } catch (e) {
      // Error ya manejado en handleCambiarEtapa
    }
  };

  const handleCancelarPerdida = () => {
    setShowMotivoPerdidaModal(false);
    setOportunidadParaPerder(null);
    setMotivoPerdida('');
  };

  const handleDragStart = (e, oportunidad) => {
    setDraggedOportunidad(oportunidad);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedOportunidad(null);
    setDragOverEtapa(null);
  };

  const handleDragOver = (e, etapa) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverEtapa(etapa);
  };

  const handleDragLeave = () => {
    setDragOverEtapa(null);
  };

  const handleDrop = async (e, nuevaEtapa) => {
    e.preventDefault();
    setDragOverEtapa(null);

    if (!draggedOportunidad) return;

    const etapaAnterior = draggedOportunidad.etapa;

    // Si la etapa no cambió, no hacer nada
    if (nuevaEtapa === etapaAnterior) {
      setDraggedOportunidad(null);
      return;
    }

    const oportunidadId = draggedOportunidad.id;

    // Actualizar optimísticamente la UI
    const oportunidadesActualizadas = oportunidades.map(o => 
      o.id === oportunidadId ? { ...o, etapa: nuevaEtapa } : o
    );
    setOportunidades(oportunidadesActualizadas);

    // Actualizar en el servidor
    try {
      if (nuevaEtapa === 'perdida') {
        // Mostrar modal para pedir motivo de pérdida
        setOportunidadParaPerder({ id: oportunidadId, etapaAnterior });
        setMotivoPerdida('');
        setShowMotivoPerdidaModal(true);
        // Revertir cambio optimista
        setOportunidades(oportunidades);
        setDraggedOportunidad(null);
        return;
      }
      
      await handleCambiarEtapa(oportunidadId, nuevaEtapa, null);
    } catch (e) {
      // Si falla, revertir el cambio local
      setOportunidades(oportunidades);
      setError('Error al cambiar la etapa. Intenta de nuevo.');
    } finally {
      setDraggedOportunidad(null);
    }
  };

  const handleDeleteOportunidad = async (id) => {
    if (!window.confirm('¿Eliminar esta oportunidad?')) return;
    try {
      await oportunidadesAPI.delete(id);
      setSuccess('Oportunidad eliminada');
      setView('pipeline');
      setSelectedOportunidad(null);
      loadOportunidades();
      loadStats();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al eliminar');
    }
  };

  const handleAddActividad = async () => {
    if (!selectedOportunidad || !formActividad.titulo.trim()) {
      setError('Título de actividad es requerido');
      return;
    }

    try {
      await actividadesAPI.create({
        oportunidad_id: selectedOportunidad.id,
        ...formActividad,
      });

      setSuccess('Actividad añadida');
      setShowActividadForm(false);
      setFormActividad({
        tipo: 'seguimiento',
        titulo: '',
        descripcion: '',
        fecha_programada: '',
      });

      const r = await oportunidadesAPI.getById(selectedOportunidad.id);
      setSelectedOportunidad(r.data);
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al añadir actividad');
    }
  };

  const handleGenerarPresupuesto = async (oportunidad) => {
    try {
      setError(null);
      const presupuestoData = {
        cliente_id: oportunidad.cliente_id,
        notas: `Generado desde oportunidad: ${oportunidad.titulo}`,
        fecha_emision: new Date().toISOString().slice(0, 10),
        lineas: [{
          descripcion: oportunidad.titulo,
          cantidad: 1,
          precio_unitario: Number(oportunidad.valor_estimado) || 0,
          iva_pct: 21,
        }],
      };
      const res = await presupuestosAPI.create(presupuestoData);
      const nuevoPresupuesto = res.data;
      const presupuestoId = nuevoPresupuesto?.id;
      if (presupuestoId) {
        await oportunidadesAPI.update(oportunidad.id, { presupuesto_id: presupuestoId });
        const r = await oportunidadesAPI.getById(oportunidad.id);
        setSelectedOportunidad(r.data);
        await loadFacturasYPresupuestos();
      }
      setSuccess('Presupuesto generado y vinculado a la oportunidad');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al generar presupuesto');
    }
  };

  const handleConvertirPresupuestoAFactura = async (oportunidad) => {
    if (!oportunidad.presupuesto_id) {
      setError('No hay presupuesto vinculado para convertir');
      return;
    }
    try {
      setError(null);
      const res = await presupuestosAPI.convertToFactura(oportunidad.presupuesto_id);
      const facturaId = res.data?.factura_id;
      if (facturaId) {
        await oportunidadesAPI.update(oportunidad.id, { factura_id: facturaId });
        const r = await oportunidadesAPI.getById(oportunidad.id);
        setSelectedOportunidad(r.data);
        await loadFacturasYPresupuestos();
      }
      setSuccess('Presupuesto convertido a factura y vinculado');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al convertir presupuesto a factura');
    }
  };

  const handleCompletarActividad = async (actividadId, resultado = null) => {
    try {
      await actividadesAPI.complete(actividadId, resultado);
      setSuccess('Actividad completada');
      const r = await oportunidadesAPI.getById(selectedOportunidad.id);
      setSelectedOportunidad(r.data);
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al completar actividad');
    }
  };

  const getEtapaColor = (etapa) => {
    const colors = {
      prospecto: '#6c757d',
      contactado: '#17a2b8',
      reunion: '#ffc107',
      propuesta: '#fd7e14',
      negociacion: '#007bff',
      ganada: '#28a745',
      perdida: '#dc3545',
      pausada: '#6f42c1',
    };
    return colors[etapa] || '#6c757d';
  };

  // Función para obtener color por prioridad (disponible para futuros usos)
  // const getPrioridadColor = (prioridad) => {
  //   const colors = {
  //     baja: '#28a745',
  //     media: '#ffc107',
  //     alta: '#fd7e14',
  //     urgente: '#dc3545',
  //   };
  //   return colors[prioridad] || '#6c757d';
  // };

  // Modal de motivo de pérdida (común a todas las vistas)
  const modalMotivoPerdida = showMotivoPerdidaModal && (
    <div className="modal-overlay" onClick={handleCancelarPerdida}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h3>Motivo de Pérdida</h3>
        <p>Indica el motivo por el que se perdió esta oportunidad <strong>(obligatorio)</strong>:</p>
        <div className="form-group">
          <textarea
            value={motivoPerdida}
            onChange={(e) => setMotivoPerdida(e.target.value)}
            rows={4}
            placeholder="Ej: Cliente eligió otra opción, precio demasiado alto, etc."
            style={{ width: '100%', padding: '10px', border: `1px solid ${!motivoPerdida.trim() ? '#dc3545' : '#ddd'}`, borderRadius: '4px' }}
            autoFocus
          />
          {!motivoPerdida.trim() && (
            <small style={{ color: '#dc3545' }}>Este campo es obligatorio</small>
          )}
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-primary" onClick={handleConfirmarPerdida} disabled={!motivoPerdida.trim()}>
            Confirmar Pérdida
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleCancelarPerdida}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );

  // Renderizar vista pipeline (kanban)
  if (view === 'pipeline') {
    const oportunidadesPorEtapa = ETAPAS.reduce((acc, etapa) => {
      acc[etapa] = oportunidades.filter(o => o.etapa === etapa);
      return acc;
    }, {});

    return (
      <>
        {modalMotivoPerdida}
      <div className="oportunidades">
        <div className="page-header">
          <h2>Pipeline de Ventas</h2>
          <div className="oportunidades-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setView('list')}>
              Vista Lista
            </button>
            <button type="button" className="btn btn-primary" onClick={handleNuevaOportunidad}>
              Nueva Oportunidad
            </button>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* Estadísticas rápidas */}
        {stats && stats.pipeline && (
          <div className="pipeline-stats">
            <div className="stat-card">
              <h4>Valor del Pipeline</h4>
              <p className="stat-value">
                {stats.pipeline.valor_ponderado !== null && stats.pipeline.valor_ponderado !== undefined
                  ? Number(stats.pipeline.valor_ponderado).toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})
                  : money(0)}
              </p>
              <small>Valor ponderado por probabilidad</small>
            </div>
            <div className="stat-card">
              <h4>Valor Total</h4>
              <p className="stat-value">
                {stats.pipeline.valor_total !== null && stats.pipeline.valor_total !== undefined
                  ? Number(stats.pipeline.valor_total).toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})
                  : money(0)}
              </p>
              <small>{stats.pipeline.total_oportunidades || 0} oportunidades activas</small>
            </div>
            <div className="stat-card">
              <h4>Ganadas Este Mes</h4>
              <p className="stat-value">{stats.mes_actual?.ganadas_mes || 0}</p>
              <small>
                {stats.mes_actual && stats.mes_actual.valor_ganado_mes !== null && stats.mes_actual.valor_ganado_mes !== undefined
                  ? Number(stats.mes_actual.valor_ganado_mes).toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})
                  : money(0)} cerrados
              </small>
            </div>
          </div>
        )}
        {stats && !stats.pipeline && (
          <div style={{ padding: '20px', background: '#fff3cd', borderRadius: '4px', marginBottom: '20px' }}>
            <p style={{ color: '#856404' }}>⚠️ No se pudieron cargar las estadísticas del pipeline</p>
          </div>
        )}

        {/* Filtros */}
        <div className="oportunidades-filters">
          <div className="filters-row">
            <div className="filter-group">
              <label>Cliente:</label>
              <select value={filtroCliente} onChange={(e) => setFiltroCliente(e.target.value)}>
                <option value="">Todos</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre} {c.apellidos}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Prioridad:</label>
              <select value={filtroPrioridad} onChange={(e) => setFiltroPrioridad(e.target.value)}>
                <option value="">Todas</option>
                {PRIORIDADES.map((p) => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Fecha desde:</label>
              <input
                type="date"
                value={filtroFechaDesde}
                onChange={(e) => setFiltroFechaDesde(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Fecha hasta:</label>
              <input
                type="date"
                value={filtroFechaHasta}
                onChange={(e) => setFiltroFechaHasta(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Origen:</label>
              <select value={filtroOrigen} onChange={(e) => setFiltroOrigen(e.target.value)}>
                <option value="">Todos</option>
                {ORIGENES.map((o) => (
                  <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <p>Cargando oportunidades...</p>
        ) : oportunidades.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <p style={{ fontSize: '18px', marginBottom: '10px' }}>No hay oportunidades creadas aún</p>
            <p style={{ fontSize: '14px' }}>Haz clic en "Nueva Oportunidad" para comenzar</p>
          </div>
        ) : (
          <div className="pipeline-board">
            {ETAPAS.map((etapa) => (
              <div 
                key={etapa} 
                className={`pipeline-column ${dragOverEtapa === etapa ? 'dragging-over' : ''}`}
                onDragOver={(e) => handleDragOver(e, etapa)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, etapa)}
              >
                <div className="column-header">
                  <h3 style={{ color: getEtapaColor(etapa) }}>
                    {etapa.charAt(0).toUpperCase() + etapa.slice(1)}
                  </h3>
                  <span className="column-count">({oportunidadesPorEtapa[etapa].length})</span>
                </div>
                <div className="column-content">
                  {oportunidadesPorEtapa[etapa].length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '13px' }}>
                      {dragOverEtapa === etapa ? 'Suelta aquí' : 'Sin oportunidades'}
                    </div>
                  ) : (
                    oportunidadesPorEtapa[etapa].map((o) => (
                      <div 
                        key={o.id} 
                        className={`oportunidad-card ${draggedOportunidad?.id === o.id ? 'dragging' : ''}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, o)}
                        onDragEnd={handleDragEnd}
                        onClick={() => handleVerOportunidad(o)}
                      >
                        <div className="card-header">
                          <h4>{o.titulo}</h4>
                          <span className={`prioridad-badge prioridad-${o.prioridad}`}>
                            {o.prioridad}
                          </span>
                        </div>
                        <p className="cliente-name">{getClienteNombre(o.cliente_id) || o.cliente_nombre + ' ' + (o.cliente_apellidos || '')}</p>
                        <div className="card-footer">
                          <span className="valor">{money(o.valor_estimado)}</span>
                          <span className="probabilidad">{Number(o.probabilidad)}%</span>
                        </div>
                        {o.fecha_estimada_cierre && (
                          <div className="fecha-cierre">
                            📅 {new Date(o.fecha_estimada_cierre).toLocaleDateString('es-ES')}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </>
    );
  }

  // Renderizar vista lista
  if (view === 'list') {
    return (
      <>
        {modalMotivoPerdida}
      <div className="oportunidades">
        <div className="page-header">
          <h2>Lista de Oportunidades</h2>
          <div className="oportunidades-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setView('pipeline')}>
              Vista Pipeline
            </button>
            <button type="button" className="btn btn-primary" onClick={handleNuevaOportunidad}>
              Nueva Oportunidad
            </button>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <div className="oportunidades-filters list-view-filters">
          <div className="filters-row filters-with-search">
            <div className="filter-group filter-search">
              <label>Búsqueda rápida</label>
              <input
                type="text"
                value={busquedaRapida}
                onChange={(e) => setBusquedaRapida(e.target.value)}
                placeholder="Título, cliente, etapa, prioridad..."
                className="search-input"
              />
            </div>
            <div className="filter-group">
              <label>Fecha desde</label>
              <input
                type="date"
                value={filtroFechaDesde}
                onChange={(e) => setFiltroFechaDesde(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Fecha hasta</label>
              <input
                type="date"
                value={filtroFechaHasta}
                onChange={(e) => setFiltroFechaHasta(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Origen</label>
              <select value={filtroOrigen} onChange={(e) => setFiltroOrigen(e.target.value)}>
                <option value="">Todos</option>
                {ORIGENES.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Cliente</label>
              <select value={filtroCliente} onChange={(e) => setFiltroCliente(e.target.value)}>
                <option value="">Todos</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre} {c.apellidos}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Prioridad</label>
              <select value={filtroPrioridad} onChange={(e) => setFiltroPrioridad(e.target.value)}>
                <option value="">Todas</option>
                {PRIORIDADES.map((p) => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
          {busquedaRapida.trim() && (
            <p className="filter-result-count">
              {oportunidadesFiltradasLista.length} de {oportunidades.length} oportunidades
            </p>
          )}
        </div>

        {loading ? (
          <p>Cargando oportunidades...</p>
        ) : oportunidadesFiltradasLista.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <p style={{ fontSize: '18px', marginBottom: '10px' }}>
              {busquedaRapida.trim() ? 'No hay resultados para tu búsqueda' : 'No hay oportunidades creadas aún'}
            </p>
            {busquedaRapida.trim() && (
              <button type="button" className="btn btn-secondary" onClick={() => setBusquedaRapida('')}>
                Limpiar búsqueda
              </button>
            )}
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table oportunidades-table">
              <thead>
                <tr>
                  <th>Título</th>
                  <th>Cliente</th>
                  <th>Valor est.</th>
                  <th>Prob.</th>
                  <th>Valor pond.</th>
                  <th>Etapa</th>
                  <th>Prioridad</th>
                  <th>Cierre est.</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {oportunidadesFiltradasLista.map((o) => (
                  <tr key={o.id} onClick={() => handleVerOportunidad(o)} style={{ cursor: 'pointer' }}>
                    <td><strong>{o.titulo}</strong></td>
                    <td>{getClienteNombre(o.cliente_id) || (o.cliente_nombre || '') + ' ' + (o.cliente_apellidos || '')}</td>
                    <td className="valor-cell">{money(o.valor_estimado)}</td>
                    <td>{Number(o.probabilidad)}%</td>
                    <td className="valor-cell valor-ponderado">{money(valorPonderado(o))}</td>
                    <td>
                      <span className={`estado-badge etapa-${o.etapa}`} style={{ backgroundColor: getEtapaColor(o.etapa) }}>
                        {o.etapa}
                      </span>
                    </td>
                    <td>
                      <span className={`prioridad-badge prioridad-${o.prioridad}`}>
                        {o.prioridad}
                      </span>
                    </td>
                    <td>{o.fecha_estimada_cierre ? new Date(o.fecha_estimada_cierre).toLocaleDateString('es-ES') : '-'}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button type="button" className="btn btn-edit btn-sm" onClick={() => handleVerOportunidad(o)}>
                        Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
    );
  }

  // Renderizar vista detalle
  if (view === 'detail' && selectedOportunidad) {
    const o = selectedOportunidad;
    const puedeEditar = o.etapa !== 'ganada' && o.etapa !== 'perdida';

    return (
      <>
        {modalMotivoPerdida}
      <div className="oportunidades">
        <div className="page-header">
          <h2>{o.titulo}</h2>
          <div className="oportunidades-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setView('pipeline')}>← Volver</button>
            {puedeEditar && (
              <>
                <button type="button" className="btn btn-success btn-sm" onClick={() => handleCambiarEtapa(o.id, 'ganada')}>
                  Marcar como Ganada
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger btn-sm" 
                  onClick={() => {
                    setOportunidadParaPerder({ id: o.id });
                    setMotivoPerdida('');
                    setShowMotivoPerdidaModal(true);
                  }}
                >
                  Marcar como Perdida
                </button>
              </>
            )}
            {puedeEditar && <button type="button" className="btn btn-primary" onClick={handleEditarOportunidad}>Editar</button>}
            {puedeEditar && <button type="button" className="btn btn-danger" onClick={() => handleDeleteOportunidad(o.id)}>Eliminar</button>}
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <div className="oportunidades-content oportunidad-detail">
          <div className="detail-header">
            <div className="detail-info">
              <p><strong>Cliente:</strong> {getClienteNombre(o.cliente_id) || o.cliente_nombre + ' ' + (o.cliente_apellidos || '')}</p>
              <p><strong>Valor estimado:</strong> {money(o.valor_estimado)}</p>
              <p><strong>Probabilidad:</strong> {Number(o.probabilidad)}%</p>
              <p><strong>Etapa:</strong> <span className={`estado-badge etapa-${o.etapa}`} style={{ backgroundColor: getEtapaColor(o.etapa) }}>{o.etapa}</span></p>
              <p><strong>Prioridad:</strong> <span className={`prioridad-badge prioridad-${o.prioridad}`}>{o.prioridad}</span></p>
              <p><strong>Origen:</strong> {o.origen}</p>
              {o.fecha_estimada_cierre && <p><strong>Cierre estimado:</strong> {new Date(o.fecha_estimada_cierre).toLocaleDateString('es-ES')}</p>}
              {o.fecha_cierre && <p><strong>Fecha de cierre:</strong> {new Date(o.fecha_cierre).toLocaleDateString('es-ES')}</p>}
              {o.factura_id && (() => {
                const fac = facturas.find(f => String(f.id) === String(o.factura_id));
                return (
                  <p><strong>Factura vinculada:</strong> 
                    <a href={`/facturacion?factura=${o.factura_id}`} style={{ marginLeft: '8px', color: '#007bff' }}>
                      {fac ? `${fac.numero || '#' + fac.id} — ${money(fac.total || 0)}` : `#${o.factura_id}`} →
                    </a>
                  </p>
                );
              })()}
              {o.presupuesto_id && (() => {
                const pres = presupuestos.find(p => String(p.id) === String(o.presupuesto_id));
                return (
                  <p><strong>Presupuesto vinculado:</strong> 
                    <a href={`/facturacion?presupuesto=${o.presupuesto_id}`} style={{ marginLeft: '8px', color: '#007bff' }}>
                      {pres ? `${pres.numero || '#' + pres.id} — ${money(pres.total || 0)}` : `#${o.presupuesto_id}`} →
                    </a>
                  </p>
                );
              })()}
            </div>
          </div>

          {/* Acciones de vinculación presupuesto / factura */}
          <div className="detail-section">
            <h3>Documentos comerciales</h3>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {!o.presupuesto_id && !o.factura_id && (
                <button 
                  type="button" 
                  className="btn btn-primary btn-sm"
                  onClick={() => handleGenerarPresupuesto(o)}
                >
                  Generar presupuesto
                </button>
              )}
              {o.presupuesto_id && !o.factura_id && (() => {
                const pres = presupuestos.find(p => String(p.id) === String(o.presupuesto_id));
                const yaConvertido = pres && pres.estado === 'convertido';
                return !yaConvertido ? (
                  <button 
                    type="button" 
                    className="btn btn-success btn-sm"
                    onClick={() => handleConvertirPresupuestoAFactura(o)}
                  >
                    Convertir presupuesto a factura
                  </button>
                ) : null;
              })()}
              {o.factura_id && (
                <span style={{ color: '#28a745', fontWeight: 500, padding: '6px 0' }}>
                  Factura ya vinculada
                </span>
              )}
            </div>
          </div>

          {o.descripcion && (
            <div className="detail-section">
              <h3>Descripción</h3>
              <p>{o.descripcion}</p>
            </div>
          )}

          {o.notas && (
            <div className="detail-section">
              <h3>Notas</h3>
              <p>{o.notas}</p>
            </div>
          )}

          {o.motivo_perdida && (
            <div className="detail-section">
              <h3>Motivo de Pérdida</h3>
              <p style={{ color: '#dc3545' }}>{o.motivo_perdida}</p>
            </div>
          )}

          {/* Actividades */}
          <div className="detail-section">
            <div className="section-header">
              <h3>Actividades ({o.actividades?.length || 0})</h3>
              {puedeEditar && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowActividadForm(!showActividadForm)}>
                  + Actividad
                </button>
              )}
            </div>

            {showActividadForm && (
              <div className="actividad-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Tipo</label>
                    <select value={formActividad.tipo} onChange={(e) => setFormActividad(f => ({ ...f, tipo: e.target.value }))}>
                      {TIPOS_ACTIVIDAD.map(t => (
                        <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Fecha programada</label>
                    <input 
                      type="datetime-local" 
                      value={formActividad.fecha_programada} 
                      onChange={(e) => setFormActividad(f => ({ ...f, fecha_programada: e.target.value }))} 
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Título *</label>
                  <input 
                    type="text" 
                    value={formActividad.titulo} 
                    onChange={(e) => setFormActividad(f => ({ ...f, titulo: e.target.value }))} 
                    placeholder="Ej: Llamada de seguimiento" 
                  />
                </div>
                <div className="form-group">
                  <label>Descripción</label>
                  <textarea 
                    value={formActividad.descripcion} 
                    onChange={(e) => setFormActividad(f => ({ ...f, descripcion: e.target.value }))} 
                    rows={3} 
                  />
                </div>
                <div className="form-actions">
                  <button type="button" className="btn btn-primary btn-sm" onClick={handleAddActividad}>Guardar</button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowActividadForm(false)}>Cancelar</button>
                </div>
              </div>
            )}

            <div className="actividades-list">
              {(o.actividades || []).map((a) => (
                <div key={a.id} className={`actividad-item ${a.estado}`}>
                  <div className="actividad-header">
                    <div className="actividad-tipo">{a.tipo}</div>
                    <div className="actividad-fecha">
                      {a.fecha_programada ? new Date(a.fecha_programada).toLocaleString('es-ES') : '-'}
                    </div>
                  </div>
                  <h4>{a.titulo}</h4>
                  {a.descripcion && <p>{a.descripcion}</p>}
                  {a.resultado && (
                    <div className="actividad-resultado">
                      <strong>Resultado:</strong> {a.resultado}
                    </div>
                  )}
                  <div className="actividad-actions">
                    {a.estado === 'pendiente' && (
                      <button 
                        type="button" 
                        className="btn btn-success btn-sm" 
                        onClick={() => {
                          const resultado = prompt('Resultado de la actividad (opcional):');
                          handleCompletarActividad(a.id, resultado);
                        }}
                      >
                        Completar
                      </button>
                    )}
                    {a.estado === 'completada' && (
                      <span className="estado-badge estado-completada">✓ Completada</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }

  // Renderizar formulario
  if (view === 'form') {
    return (
      <>
        {modalMotivoPerdida}
      <div className="oportunidades">
        <div className="page-header">
          <h2>{selectedOportunidad ? 'Editar oportunidad' : 'Nueva oportunidad'}</h2>
          <div className="oportunidades-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setView('pipeline')}>← Volver</button>
            <button type="button" className="btn btn-primary" onClick={handleSaveOportunidad}>Guardar</button>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <div className="oportunidades-content oportunidad-form">
          <div className="form-row">
            <div className="form-group">
              <label>Cliente *</label>
              <select value={form.cliente_id} onChange={(e) => setForm(f => ({ ...f, cliente_id: e.target.value }))}>
                <option value="">Seleccionar...</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre} {c.apellidos}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Título *</label>
              <input 
                type="text" 
                value={form.titulo} 
                onChange={(e) => setForm(f => ({ ...f, titulo: e.target.value }))} 
                placeholder="Ej: Coaching ejecutivo empresa X" 
              />
            </div>
          </div>

          <div className="form-group">
            <label>Descripción</label>
            <textarea 
              value={form.descripcion} 
              onChange={(e) => setForm(f => ({ ...f, descripcion: e.target.value }))} 
              rows={3} 
              placeholder="Detalles de la oportunidad"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Valor estimado ({currency.code})</label>
              <input 
                type="number" 
                step="0.01" 
                min="0" 
                value={form.valor_estimado} 
                onChange={(e) => setForm(f => ({ ...f, valor_estimado: e.target.value }))} 
              />
            </div>
            <div className="form-group">
              <label>Probabilidad (%)</label>
              <input 
                type="number" 
                step="1" 
                min="0" 
                max="100" 
                value={form.probabilidad} 
                onChange={(e) => setForm(f => ({ ...f, probabilidad: e.target.value }))} 
              />
            </div>
            <div className="form-group">
              <label>Fecha estimada de cierre</label>
              <input 
                type="date" 
                value={form.fecha_estimada_cierre} 
                onChange={(e) => setForm(f => ({ ...f, fecha_estimada_cierre: e.target.value }))} 
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Etapa</label>
              <select value={form.etapa} onChange={(e) => setForm(f => ({ ...f, etapa: e.target.value }))}>
                {ETAPAS.map(e => (
                  <option key={e} value={e}>{e.charAt(0).toUpperCase() + e.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Origen</label>
              <select value={form.origen} onChange={(e) => setForm(f => ({ ...f, origen: e.target.value }))}>
                {ORIGENES.map(o => (
                  <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Prioridad</label>
              <select value={form.prioridad} onChange={(e) => setForm(f => ({ ...f, prioridad: e.target.value }))}>
                {PRIORIDADES.map(p => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Notas</label>
            <textarea 
              value={form.notas} 
              onChange={(e) => setForm(f => ({ ...f, notas: e.target.value }))} 
              rows={4} 
              placeholder="Notas adicionales sobre la oportunidad"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Factura vinculada</label>
              <select value={form.factura_id} onChange={(e) => setForm(f => ({ ...f, factura_id: e.target.value }))}>
                <option value="">Sin factura</option>
                {facturas.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.numero || `#${f.id}`} — {money(f.total || 0)} ({f.estado || 'borrador'})
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Presupuesto vinculado</label>
              <select value={form.presupuesto_id} onChange={(e) => setForm(f => ({ ...f, presupuesto_id: e.target.value }))}>
                <option value="">Sin presupuesto</option>
                {presupuestos.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.numero || `#${p.id}`} — {money(p.total || 0)} ({p.estado || 'borrador'})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      {modalMotivoPerdida}
      <div className="oportunidades">
        <div>Vista no implementada</div>
      </div>
    </>
  );
}

export default Oportunidades;